import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import colors from 'src/utils/colors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Divider, Box } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { SlPill } from 'src/components/BeaconV2Components/SlPill';
import { AppImage, brandclub_colors, useStacklineTheme } from '@stackline/ui';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { AlertTemplateType, AlertTemplateIdType } from '../schema/template';
import { AlertsTabsPath } from '../utils';

interface AlertTemplateCardProps {
  template: AlertTemplateType;
  isSelected?: boolean;
  onSelect?: (template: AlertTemplateType) => void;
  disabled?: boolean;
}

export const AlertTemplateCard: React.FC<AlertTemplateCardProps> = ({ 
  template, 
  isSelected, 
  onSelect, 
  disabled 
}) => {
  const { beaconNavigator } = useBeaconNavigator();

  const handleSelectTemplate = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Call the onSelect callback if provided (for local state management)
    if (onSelect) {
      onSelect(template);
    }
    
    // Navigate directly to create alert page with template ID
    beaconNavigator.setPathname(AlertsTabsPath.createAlert);
    beaconNavigator.updateSearchParams({ templateId: template.id as AlertTemplateIdType });
    beaconNavigator.go();
  };
  
  const Pill = ({ text }: { text: string }) => (
    <SlPill
      sx={{
        backgroundColor: '#F2F4F6',
        borderRadius: '4px',
        border: 'none',
        height: 'auto',
        cursor: 'default',
        padding: '4px 8px'
      }}
    >
      <Text variant="body3" fontWeight="500" sx={{ color: colors.grey }}>
        {text}
      </Text>
    </SlPill>
  );

  return (
    <Flex
      gap={16}
      flexDirection="column"
      sx={{
        border: `1px solid`,
        borderColor: isSelected ? colors.actualSpend : colors.graysSecondary,
        borderRadius: '8px',
        boxSizing: 'content-box',
        padding: '16px',
        lineHeight: '18px'
      }}
    >
      <Flex flexDirection="column" gap={8}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text variant="h4" sx={{ fontSize: '16px' }}>
            {template.title}
          </Text>
        </Flex>
        <Text 
          variant="body2" 
          lineHeight="20px" 
          sx={{ 
            fontSize: '14px', 
            height: '40px', 
            overflow: 'hidden',
            color: 'var(--primary-031425, #031425)',
            fontFeatureSettings: "'liga' off, 'clig' off",
            textOverflow: 'ellipsis',
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            letterSpacing: '0.08px'
          }}
        >
          {template.description}
        </Text>
      </Flex>
      
      <Flex gap={8}>
        {!template.valid ? <Pill text="Coming soon" /> : template.tags?.map((tag) => <Pill key={tag} text={tag} />)}
      </Flex>
      <Divider />
      <Flex justifyContent="space-between" alignItems="center">
        {!template.valid ? (
          <Text variant="body3" sx={{ color: colors.grey, lineHeight: '24px' }}>
            More details on the way
          </Text>
        ) : (
          <>
            <Text variant="body3" sx={{ color: colors.grey, lineHeight: '24px' }}>
              {template.types?.join(' | ') || ''}
            </Text>
            <SlButton
              buttonSx={{
                borderRadius: '16px',
                padding: '3px 20px',
                height: '24px',
                backgroundColor: 'transparent',
                color: colors.darkBlue,
                borderColor: colors.graysSecondary,
                '&:hover': { backgroundColor: colors.darkBlue, borderColor: colors.darkBlue, color: colors.white }
              }}
              textSx={{
                fontSize: '12px',
                lineHeight: '18px',
                color: 'inherit',
              }}
              onClick={handleSelectTemplate}
              disabled={disabled}
            >
              Create
            </SlButton>
          </>
        )}
      </Flex>
    </Flex>
  );
};

// Search input component to match PresentationsTypePage styling
export const AlertSearchInput = (props: TextFieldProps) => {
  const theme = useStacklineTheme();

  return (
    <TextField
      variant="standard"
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <AppImage
            src="/svg/icons/search.svg"
            alt="search-icon"
            style={{
              marginRight: 10,
              height: 13,
              width: 13
            }}
          />
        ),
        autoComplete: 'off',
        id: 'alert-template-search'
      }}
      sx={{
        borderRadius: 17,
        border: `1px solid ${theme.colors.primaryGray}`,
        padding: '5px 14px',
        width: 'fit-content',
        backgroundColor: brandclub_colors.white,
        '& div.MuiInput-root': {
          display: 'flex',
          alignItems: 'center',
          height: '20px',
          boxSizing: 'content-box',
          width: '262px',
          '& input': {
            padding: 0,
            fontSize: '14px',
            color: theme.colors.primary
          }
        },
        '.MuiInput-input::placeholder': {
          color: theme.colors.secondary,
          fontSize: '14px'
        }
      }}
      {...props}
    />
  );
};

// Grid container for templates
export const AlertTemplateGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '16px'
    }}
  >
    {children}
  </Box>
);

// Search row container
export const AlertSearchRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex sx={{ marginBottom: '16px' }}>
    {children}
  </Flex>
); 